import * as THREE from 'three/webgpu';
import { Fn, attribute } from 'three/tsl';
import { sampleGround } from '../terrain/heightfield.js';
import { WHEELS } from '../vehicle/jeep.js';

const MAX_MARKS = 512;
const VERT_COUNT = MAX_MARKS * 6; // 2 triangles (6 vertices) per quad

export class Skidmarks {
  constructor(scene) {
    this.positions = new Float32Array(VERT_COUNT * 3);
    this.opacities = new Float32Array(VERT_COUNT);
    this.index = 0;
    this.count = 0;

    this.geom = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(this.positions, 3);
    this.opAttr = new THREE.BufferAttribute(this.opacities, 1);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.opAttr.setUsage(THREE.DynamicDrawUsage);

    this.geom.setAttribute('position', this.posAttr);
    this.geom.setAttribute('opacity', this.opAttr);

    const mat = new THREE.MeshBasicNodeMaterial({
      color: 0x141414,
      transparent: true,
      depthWrite: false
    });
    mat.opacityNode = Fn(() => {
      return attribute('opacity').mul(0.68);
    })();

    this.mesh = new THREE.Mesh(this.geom, mat);
    this.mesh.renderOrder = 2;
    scene.add(this.mesh);

    this.lastPositions = [null, null, null, null];
    this.emitTimer = 0;
  }

  update(dt, jeep, input) {
    // 1. Fade existing skidmarks
    let needsUpdate = false;
    const fadeRate = dt * 0.12; // Fades in ~8.3 seconds
    for (let i = 0; i < this.count; i++) {
      const vertBase = i * 6;
      const op = this.opacities[vertBase];
      if (op > 0) {
        const nextOp = Math.max(0, op - fadeRate);
        for (let k = 0; k < 6; k++) {
          this.opacities[vertBase + k] = nextOp;
        }
        needsUpdate = true;
      }
    }

    // 2. Check if vehicle is slipping or locking brakes
    const hardBrake = (input.brake && jeep.speed > 3) || input.handbrake;
    const isDrifting = jeep.slip > 0.16 || hardBrake;

    this.emitTimer += dt;
    if (this.emitTimer >= 0.04) {
      this.emitTimer = 0;

      if (isDrifting && jeep.onGround) {
        const sinYaw = Math.sin(jeep.yaw);
        const cosYaw = Math.cos(jeep.yaw);

        // Emit under rear wheels (indices 2, 3) and front if hard drift
        const wheelsToCheck = jeep.slip > 0.35 ? [0, 1, 2, 3] : [2, 3];

        for (const wIdx of wheelsToCheck) {
          const w = WHEELS[wIdx];
          const ox = w.x * sinYaw + w.z * cosYaw;
          const oz = w.x * cosYaw - w.z * sinYaw;
          const wx = jeep.pos.x + ox;
          const wz = jeep.pos.z + oz;

          const g = sampleGround(wx, wz);
          const wy = g.height + 0.04; // Elevate slightly above terrain
          const currentPoint = { x: wx, y: wy, z: wz };

          const last = this.lastPositions[wIdx];
          if (last) {
            const dist = Math.hypot(currentPoint.x - last.x, currentPoint.z - last.z);
            if (dist > 0.3 && dist < 5.0) {
              this.addSegment(last, currentPoint, jeep.slip);
              needsUpdate = true;
            }
          }
          this.lastPositions[wIdx] = currentPoint;
        }
      } else {
        this.lastPositions = [null, null, null, null];
      }
    }

    if (needsUpdate) {
      this.posAttr.needsUpdate = true;
      this.opAttr.needsUpdate = true;
    }
  }

  addSegment(p0, p1, slipFactor) {
    const dx = p1.x - p0.x;
    const dz = p1.z - p0.z;
    const len = Math.hypot(dx, dz) || 1;
    // Normal vector perpendicular to track segment
    const halfWidth = 0.18;
    const nx = (-dz / len) * halfWidth;
    const nz = (dx / len) * halfWidth;

    const slot = this.index;
    this.index = (this.index + 1) % MAX_MARKS;
    if (this.count < MAX_MARKS) this.count++;

    const baseV = slot * 6;
    const baseP = baseV * 3;

    // Quad: 2 triangles (v0, v1, v2) and (v2, v1, v3)
    const x0L = p0.x - nx, z0L = p0.z - nz;
    const x0R = p0.x + nx, z0R = p0.z + nz;
    const x1L = p1.x - nx, z1L = p1.z - nz;
    const x1R = p1.x + nx, z1R = p1.z + nz;

    const y0 = p0.y;
    const y1 = p1.y;

    const op = Math.min(1.0, 0.45 + slipFactor * 0.55);

    // Triangle 1: (0L, 0R, 1L)
    this.positions[baseP + 0] = x0L; this.positions[baseP + 1] = y0; this.positions[baseP + 2] = z0L;
    this.positions[baseP + 3] = x0R; this.positions[baseP + 4] = y0; this.positions[baseP + 5] = z0R;
    this.positions[baseP + 6] = x1L; this.positions[baseP + 7] = y1; this.positions[baseP + 8] = z1L;

    // Triangle 2: (1L, 0R, 1R)
    this.positions[baseP + 9]  = x1L; this.positions[baseP + 10] = y1; this.positions[baseP + 11] = z1L;
    this.positions[baseP + 12] = x0R; this.positions[baseP + 13] = y0; this.positions[baseP + 14] = z0R;
    this.positions[baseP + 15] = x1R; this.positions[baseP + 16] = y1; this.positions[baseP + 17] = z1R;

    for (let k = 0; k < 6; k++) {
      this.opacities[baseV + k] = op;
    }
  }
}
